'use client';
import Button from '@/components/Button';
import Input from '@/components/Input';
import { Modal } from '@/components/Modal/Modal';
import ArrowLeftCircle from '@/components/icons/ArrowLeftCircle';
import { NotifsContext } from '@/contexts';
import useNavigate from '@/hooks/useNavigate';
import {
  ReqModifierPDV,
  useEditPdvMutation,
  useGetPdvQuery,
} from '@/services/API';
import { useAppSelector } from '@/store/hooks';
import form from '@/styles/form.module.css';
import { FormInput } from '@/types';
import { getEmptyError } from '@/types/Input';
import { getURLParams } from '@/utils/router';
import { useContext, useEffect, useState } from 'react';

export default function Page() {
  const siren = useAppSelector((state) => state.selectedSte.societe.siren);
  const navigate = useNavigate();
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const { addToast } = useContext(NotifsContext);

  const [libele, setLibele] = useState<FormInput>({
    value: '',
    error_msg: '',
  });

  const [idExt, setIdExt] = useState<FormInput>({
    value: '',
    error_msg: '',
  });

  const searchParams = getURLParams();
  const {
    data: oldPdv,
    isLoading,
    isError,
  } = useGetPdvQuery({
    siren,
    idPdv: searchParams.get('id') || '',
  });

  const [modifierPdv, { isLoading: isLoadingUpdate }] = useEditPdvMutation();

  const handleSubmit = () => {
    const request: ReqModifierPDV = {
      siren: siren,
      posid: searchParams.get('id') || '',
      reqbody: { name: libele.value, partner_pos_id: idExt.value },
    };

    modifierPdv(request)
      .unwrap()
      .then(() => {
        addToast &&
          addToast({
            toastType: 'success',
            children: 'Confirmation de modification.',
          });
      })
      .catch(() => {
        addToast &&
          addToast({
            toastType: 'error',
            children: `Une erreur est survenue.
            Rééssayer ultérieurement.`,
          });
      })
      .finally(() => setOpenConfirmModal(false));
  };

  useEffect(() => {
    setLibele({ ...libele, value: oldPdv?.name || '' });
    setIdExt({ ...idExt, value: oldPdv?.partner_pos_id || '' });
  }, [oldPdv]);

  if (isLoading || isError || !oldPdv) return <></>;
  return (
    <div className={`flex-column gap-l ${form.content}`}>
      <Button
        variant="ghost"
        icon={<ArrowLeftCircle />}
        onClick={() => navigate(-1)}
      >
        Retour
      </Button>
      <form className={`${form.form} gap-l`}>
        <h3 className={form.title}>Modifier un numéro d’agrément</h3>
        <Input
          label="Numéro agrément / union / regroupement / chaîne"
          placeholder="XXXX"
          helperText="10 chiffres ( Société commerciale + Agrément )"
          disabled
          value={oldPdv?.bnppf_pos_id || ''}
        />
        <Input
          maxLength={50}
          label="Libellé (*)"
          placeholder="Libellé"
          onChange={(e) => {
            setLibele((prev) => ({ ...prev, value: e.target.value }));
          }}
          errorMessage={libele.error_msg}
          onBlur={(e) => {
            setLibele((prev) => ({
              ...prev,
              error_msg: getEmptyError(e.target.value),
            }));
          }}
          value={libele.value}
        />
        <Input
          label="Identifiant externe partenaire"
          placeholder="XXXX"
          /*onChange={(e) => {}}*/
          /* disabled */
          onChange={(e) => {
            setIdExt((prev) => ({ ...prev, value: e.target.value }));
          }}
          errorMessage={idExt.error_msg}
          value={idExt.value}
        />
        <p className="xs">* Champs obligatoires</p>
        <Button
          disabled={libele.value === ''}
          onClick={(e) => {
            e.preventDefault();
            setOpenConfirmModal(true);
          }}
        >
          Modifier le numéro d’agrément
        </Button>
      </form>
      <Modal
        primaryBtnProps={{
          children: 'Confirmer',
          onClick: handleSubmit,
          loading: isLoadingUpdate,
          disabled: isLoadingUpdate,
        }}
        secondaryBtnProps={{
          children: 'Annuler',
          onClick: () => setOpenConfirmModal(false),
          disabled: isLoadingUpdate,
        }}
        title="Voulez-vous modifier le numéro d’agrément ?"
        open={openConfirmModal}
        onClose={() => setOpenConfirmModal(false)}
      />
    </div>
  );
}
